import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { opsService, type NotificationSettings } from '../../services/opsService';
import { useAuth } from '../../context/AuthContext';

const empty: NotificationSettings = {
  mattermostEnabled: true,
  channel: 'alerts',
  notifyInfo: false,
  notifyWarning: true,
  notifyCritical: true,
  notifySecurity: true,
  notifyCleanup: true,
};

export function InfrastructureNotificationsPage() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('manage_ops') || hasPermission('ops.write');
  const [s, setS] = useState<NotificationSettings>(empty);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setS({ ...empty, ...(await opsService.getNotifications()) });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!canWrite) return;
    setBusy(true);
    try {
      setS(await opsService.putNotifications(s));
      toast.success('ذخیره شد');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">اعلان‌های Ops</h1>
        <p className="mt-1 text-sm text-slate-500">
          وبهوک Mattermost فقط روی سرور عامل تنظیم می‌شود و اینجا ذخیره نمی‌گردد.
        </p>
      </div>

      {loading ? (
        <div className="rounded-xl bg-white p-8 text-center text-slate-500 shadow-sm">در حال بارگذاری…</div>
      ) : (
        <section className="space-y-4 rounded-xl bg-white p-5 shadow-sm">
          <div className="rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-700">
            {s.note || 'آدرس وبهوک در env عامل است.'}
            <div className="mt-1 text-xs text-slate-500">
              وضعیت توکن عامل API: {s.webhookConfigured ? 'پیکربندی شده' : 'پیکربندی نشده'}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={s.mattermostEnabled}
              disabled={!canWrite}
              onChange={(e) => setS({ ...s, mattermostEnabled: e.target.checked })}
            />
            ارسال به Mattermost فعال باشد
          </label>

          <label className="block text-sm">
            کانال پیشنهادی
            <input
              className="mt-1 w-full max-w-md rounded-lg border border-slate-200 px-3 py-2"
              value={s.channel}
              disabled={!canWrite}
              onChange={(e) => setS({ ...s, channel: e.target.value })}
            />
          </label>

          <div className="grid gap-2 sm:grid-cols-2">
            {(
              [
                ['notifyInfo', 'INFO'],
                ['notifyWarning', 'WARNING'],
                ['notifyCritical', 'CRITICAL'],
                ['notifySecurity', 'SECURITY'],
                ['notifyCleanup', 'Cleanup'],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={s[key]}
                  disabled={!canWrite}
                  onChange={(e) => setS({ ...s, [key]: e.target.checked })}
                />
                {label}
              </label>
            ))}
          </div>

          {canWrite ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => void save()}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              ذخیره تنظیمات
            </button>
          ) : null}
        </section>
      )}
    </div>
  );
}
